import { Button } from '@optimus-web/core';
import { FloatingPanel } from '@mews/b2b-ui';
import { UserProfileResult } from '@mews-commander/data-access';
import { LocalizedText, useDialog, useFormControls } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { OptimusProvider, UnsavedChangesDialog, useDialogs } from '@mews-commander/ui';
import { useUserContext } from '../../context';
import { UserType } from '../../types';
import { useEditUserForm } from './useEditUserForm';
import { useEditEnterpriseProfileForm } from './useEditEnterpriseProfileForm';
import { UserEditContent } from './UserEditContent';
import { EditUserFormValues } from './schema';

interface DialogProps {
    onClose: () => void;
    userProfile: UserProfileResult;
}

interface FormProps extends ReturnType<typeof useFormControls<EditUserFormValues>> {
    isPending: boolean;
}

const Dialog = ({
    renderFormContent,
    submit,
    isPending,
    hasDirtyFields,
    userProfile,
    onClose,
}: FormProps & DialogProps) => {
    const [{ active: showUnsavedChangesDialog }, toggleUnsavedChangesDialog] = useDialog();

    const handleClose = () => {
        if (hasDirtyFields) {
            toggleUnsavedChangesDialog();
        } else {
            onClose();
        }
    };

    return renderFormContent(
        <OptimusProvider density="spacious">
            <FloatingPanel
                active
                onClose={handleClose}
                loading={isPending}
                title={
                    <LocalizedText
                        textKey={TextKey.UserProfileTitle}
                        substitutes={{ name: userProfile.fullName || '' }}
                    />
                }
                footer={{
                    primary: (
                        <Button type="submit" variant="primary" onClick={submit}>
                            <LocalizedText textKey={TextKey.SaveChanges} />
                        </Button>
                    ),
                }}
            >
                <UserEditContent userProfile={userProfile} isLoading={isPending} />
            </FloatingPanel>
            <UnsavedChangesDialog
                active={showUnsavedChangesDialog}
                content={
                    <LocalizedText
                        textKey={TextKey.UserEditUnsavedChangesDescription}
                        substitutes={{ name: userProfile.fullName || '' }}
                    />
                }
                onCancel={toggleUnsavedChangesDialog}
                onDiscardChanges={() => {
                    onClose();
                    toggleUnsavedChangesDialog();
                }}
            />
        </OptimusProvider>
    );
};

export const EditUserDialog = ({ userProfile, onClose }: DialogProps) => {
    const formProps = useEditUserForm({ userProfile, onSuccess: onClose });

    return <Dialog {...formProps} userProfile={userProfile} onClose={onClose} />;
};

export const EditEnterpriseProfileDialog = ({ userProfile, onClose }: DialogProps) => {
    const formProps = useEditEnterpriseProfileForm({
        userProfile,
        onSuccess: onClose,
    });

    return <Dialog {...formProps} userProfile={userProfile} onClose={onClose} />;
};

export const useOpenEditUserDialog = (props: { userProfile: UserProfileResult }) => {
    const { openDialog, closeDialog } = useDialogs();
    const { userType } = useUserContext();
    const EDIT_USER_DIALOG_NAME = 'editUser';
    const onClose = () => closeDialog(EDIT_USER_DIALOG_NAME);

    return userType === UserType.Portfolio
        ? () => openDialog(<EditEnterpriseProfileDialog {...props} onClose={onClose} />, EDIT_USER_DIALOG_NAME)
        : () => openDialog(<EditUserDialog {...props} onClose={onClose} />, EDIT_USER_DIALOG_NAME);
};
